import React, { useState, useEffect } from 'react';
import { AlertTriangle, WifiOff } from 'lucide-react';
import { isNetworkError } from '../../utils/formErrorHandler';
import { useI18n } from '../../i18n/I18nContext';
import './FormErrorDisplay.css';

/**
 * Form error banner with a retry button and backoff feedback (spec §3.12).
 *
 * `error` only decides WHICH banner shows (a lost connection, or a failed request): the words
 * are always the app's own translated copy — the server's / axios' text is never printed.
 * Pass `message` for a more specific, already-translated sentence.
 *
 *   <FormErrorDisplay error={err} message={t('settings.saveFailed')} onRetry={retry} retryCount={n} />
 */
const FormErrorDisplay = ({
  error,
  message,
  onRetry,
  retryCount = 0,
  maxRetries = 3,
  isRetrying = false,
  autoRetryDelay = null
}) => {
  const { t } = useI18n();
  const [nextRetryIn, setNextRetryIn] = useState(null);
  const [isNetworkDown, setIsNetworkDown] = useState(false);

  useEffect(() => {
    if (error) {
      setIsNetworkDown(isNetworkError(error));
    }
  }, [error]);

  // Handle auto-retry countdown
  useEffect(() => {
    if (!autoRetryDelay) return undefined;

    setNextRetryIn(Math.ceil(autoRetryDelay / 1000));

    const interval = setInterval(() => {
      setNextRetryIn(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [autoRetryDelay]);

  if (!error) return null;

  const isMaxRetriesReached = retryCount >= maxRetries;
  const Icon = isNetworkDown ? WifiOff : AlertTriangle;
  // Developers still see what went wrong, in the console-adjacent dev build only.
  const raw = process.env.NODE_ENV === 'development'
    ? (typeof error === 'string' ? error : error.message)
    : null;

  return (
    <div
      className={`fed ui-alert ${isNetworkDown ? 'network-error' : 'server-error'}`}
      data-tone={isNetworkDown ? 'warning' : 'danger'}
      role="alert"
    >
      <Icon aria-hidden="true" />

      <div className="fed-content">
        <p className="fed-title">
          {isNetworkDown ? t('ui.connectionLost') : t('ui.somethingWentWrong')}
        </p>

        <p className="fed-text">{message || t('ui.errorDescription')}</p>

        {isNetworkDown && (
          <p className="fed-subtext">{t('ui.willRetryWhenOnline')}</p>
        )}

        {!isNetworkDown && retryCount > 0 && (
          <p className="fed-subtext">{t('ui.retryAttempt', { count: retryCount })}</p>
        )}

        {raw && <p className="fed-subtext fed-dev">{raw}</p>}
      </div>

      <div className="fed-actions">
        {nextRetryIn !== null && (
          <div className="fed-chip">{t('ui.retryingIn', { seconds: nextRetryIn })}</div>
        )}

        {!isMaxRetriesReached && (
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            className={`ds-btn ds-btn-sm ${isNetworkDown ? 'ds-btn-primary' : 'ds-btn-danger-fill'}${isRetrying ? ' is-loading' : ''}`}
            aria-busy={isRetrying || undefined}
          >
            {isRetrying ? t('ui.retrying') : t('ui.retry')}
          </button>
        )}

        {isMaxRetriesReached && (
          <div className="fed-chip">{t('ui.maxRetriesReached')}</div>
        )}
      </div>
    </div>
  );
};

export default FormErrorDisplay;
